import { useState, useEffect, useRef } from "react"
import {
  UserPlus,
  Trash2,
  X,
  Search,
  Edit,
  MoreVertical,
  Check,
  AlertCircle,
  Loader2,
  ChevronDown,
} from "lucide-react"
import ReactDOM from "react-dom"
import { useAuth } from "../contexts/AuthProvider"
import OrganizationService from "../services/OrganizationService"
import InvitationService from "../services/InvitationService"
import AddUserModal from "./AddUserModal"
import ProfileSidebar from "./ProfileSideBar"

// ── Design tokens (mirrors InterviewsTab) ────────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border:       "0.5px solid #e5e7eb",
  borderHover:  "0.5px solid #d1d5db",
  radius:       8,
  radiusCard:   10,
  bg:           "#fff",
  bgPage:       "#f5f5f0",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

const DESKTOP_SIDEBAR_WIDTH = "15rem"
const TABLE_MIN_WIDTH = 860

const GRID_COLS = "minmax(0,1.6fr) minmax(0,2fr) minmax(0,0.8fr) minmax(0,0.8fr) 100px"
const HEADERS   = ["Member", "Email", "Role", "Status", ""]

// ── Helpers ───────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "?"

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#0ea5e9,#6366f1)",
  "linear-gradient(135deg,#10b981,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
  "linear-gradient(135deg,#14b8a6,#6366f1)",
  "linear-gradient(135deg,#f97316,#eab308)",
  "linear-gradient(135deg,#06b6d4,#10b981)",
]
const getAvatarGradient = (name = "") =>
  AVATAR_GRADIENTS[name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length]

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ name }) => (
  <div style={{
    width: 32, height: 32, borderRadius: 7,
    background: getAvatarGradient(name),
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, color: "#fff",
    fontSize: 11, fontWeight: 700, fontFamily: FF, letterSpacing: "0.02em",
  }}>
    {getInitials(name)}
  </div>
)

// ── Role / Status badge configs ───────────────────────────────────────────────
const ROLE_CFG = {
  admin:       { bg: "#f5f3ff", color: "#5b21b6", border: "#ddd6fe" },
  interviewer: { bg: "#eef2ff", color: "#4338ca", border: "#c7d2fe" },
  recruiter:   { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0" },
  member:      { bg: "#f9fafb", color: "#374151", border: "#e5e7eb" },
}
const STATUS_CFG = {
  active:  { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0" },
  pending: { bg: "#fffbeb", color: "#92400e", border: "#fde68a" },
}

const getRoleCfg  = (role)   => ROLE_CFG[role]   || ROLE_CFG.member
const getStatusCfg = (status) => STATUS_CFG[status] || STATUS_CFG.active

// ── Table shimmer ─────────────────────────────────────────────────────────────
const TableShimmer = ({ rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{
        display: "grid", gridTemplateColumns: GRID_COLS,
        padding: "13px 20px",
        borderBottom: i < rows - 1 ? T.border : "none",
        alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: 7, background: "#f3f4f6" }} />
          <div style={{ height: 13, width: "55%", background: "#f3f4f6", borderRadius: 4 }} />
        </div>
        <div style={{ height: 12, width: "70%", background: "#f3f4f6", borderRadius: 4 }} />
        <div style={{ height: 22, width: 72, background: "#f3f4f6", borderRadius: T.radius }} />
        <div style={{ height: 22, width: 60, background: "#f3f4f6", borderRadius: T.radius }} />
        <div style={{ height: 28, width: 40, background: "#f3f4f6", borderRadius: T.radius, marginLeft: "auto" }} />
      </div>
    ))}
  </>
)

// ── Table header ──────────────────────────────────────────────────────────────
const TableHeader = () => (
  <div style={{
    display: "grid", gridTemplateColumns: GRID_COLS,
    padding: "7px 20px", borderBottom: T.border, background: "#fdfcfb",
  }}>
    {HEADERS.map((h, i) => (
      <span key={i} style={{
        fontSize: 12, fontWeight: 700, color: T.textTertiary,
        textTransform: "uppercase", letterSpacing: "0.07em",
        fontFamily: MONO,
        textAlign: i === HEADERS.length - 1 ? "right" : "left",
        display: "flex", alignItems: "center", minHeight: 36,
      }}>
        {h}
      </span>
    ))}
  </div>
)

// ── Dropdown item helper ──────────────────────────────────────────────────────
const DropdownItem = ({ icon: Icon, label, color, hoverBg = "#252e40", onClick }) => (
  <button
    className="org-menu"
    onClick={onClick}
    style={{
      width: "100%", display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", border: "none",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
      background: "transparent", cursor: "pointer",
      fontFamily: FF, transition: "background 0.1s", outline: "none",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
  >
    <Icon size={13} color={color} style={{ flexShrink: 0 }} />
    <span style={{ fontSize: 12, fontWeight: 700, color, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: MONO }}>
      {label}
    </span>
  </button>
)

// ── Member row ────────────────────────────────────────────────────────────────
const MemberRow = ({
  member, isCurrentUser, isAdmin, isLast,
  openDropdownId, dropdownPos, onOpenMenu, onCloseMenu,
  onEdit, onRemove,
}) => {
  const name    = member.name || member.email.split("@")[0]
  const roleCfg = getRoleCfg(member.role)
  const stsCfg  = getStatusCfg(member.status || "active")

  return (
    <div style={{
      display: "grid", gridTemplateColumns: GRID_COLS,
      padding: "11px 20px", alignItems: "center",
      borderBottom: isLast ? "none" : T.border,
      transition: "background 0.1s", background: "transparent",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.background = T.bgMuted }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
    >
      {/* Member */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, paddingRight: 16 }}>
        <Avatar name={name} />
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: FF }}>
            {name}
            {isCurrentUser && (
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: T.textTertiary, fontFamily: MONO }}>(you)</span>
            )}
          </p>
        </div>
      </div>

      {/* Email */}
      <div style={{ minWidth: 0, paddingRight: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", fontFamily: FF }}>
          {member.email}
        </span>
      </div>

      {/* Role badge */}
      <div>
        <span style={{
          display: "inline-flex", alignItems: "center",
          padding: "3px 8px", borderRadius: T.radius,
          background: roleCfg.bg, color: roleCfg.color,
          border: `0.5px solid ${roleCfg.border}`,
          fontSize: 12, fontWeight: 600, fontFamily: MONO,
          whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.04em",
        }}>
          {member.role || "member"}
        </span>
      </div>

      {/* Status badge */}
      <div>
        <span style={{
          display: "inline-flex", alignItems: "center",
          padding: "3px 8px", borderRadius: T.radius,
          background: stsCfg.bg, color: stsCfg.color,
          border: `0.5px solid ${stsCfg.border}`,
          fontSize: 12, fontWeight: 600, fontFamily: MONO,
          whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.04em",
        }}>
          {member.status || "active"}
        </span>
      </div>

      {/* Actions */}
      <div
        className="org-menu"
        style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 5, position: "relative" }}
        onClick={(e) => e.stopPropagation()}
      >
        {isAdmin && !isCurrentUser ? (
          <>
            <button
              className="org-menu"
              onClick={(e) => onOpenMenu(member.uid, e)}
              style={{
                width: 28, height: 28, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: T.bg, border: T.border,
                borderRadius: T.radius - 1, cursor: "pointer",
                color: T.textTertiary, transition: "all 0.12s", outline: "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#111827"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#111827" }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.textTertiary; e.currentTarget.style.borderColor = "#e5e7eb" }}
            >
              <MoreVertical size={12} />
            </button>

            {openDropdownId === member.uid && dropdownPos &&
              ReactDOM.createPortal(
                <div
                  className="org-menu"
                  style={{
                    position: "fixed", zIndex: 9999,
                    top: dropdownPos.top, bottom: dropdownPos.bottom,
                    right: dropdownPos.right,
                    width: 180, overflow: "hidden",
                    background: "#1e2535",
                    borderRadius: T.radiusCard,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                  }}
                >
                  <DropdownItem
                    icon={Edit}
                    label="Edit Role"
                    color="#8b95aa"
                    onClick={() => { onEdit(member); onCloseMenu() }}
                  />
                  <DropdownItem
                    icon={Trash2}
                    label="Remove"
                    color="#f87171"
                    hoverBg="#2d1f1f"
                    onClick={() => { onRemove(member.uid, member.email); onCloseMenu() }}
                  />
                </div>,
                document.body,
              )
            }
          </>
        ) : (
          <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>—</span>
        )}
      </div>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ searchQuery, onClear }) => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "96px 24px", textAlign: "center",
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: T.radiusCard,
      border: T.border, background: "#f9fafb",
      display: "flex", alignItems: "center", justifyContent: "center",
      marginBottom: 16, boxShadow: T.shadow,
    }}>
      <Search size={18} color="#d1d5db" />
    </div>
    <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6, fontFamily: FF, letterSpacing: "-0.01em" }}>
      {searchQuery ? "No matches found" : "No team members yet"}
    </p>
    <p style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, marginBottom: 20, fontFamily: FF }}>
      {searchQuery ? "Try a different search term" : "Invite members to get started"}
    </p>
    {searchQuery && (
      <button
        onClick={onClear}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 34, padding: "0 14px", borderRadius: T.radius,
          border: T.border, background: T.bg, color: T.textMuted,
          fontSize: 12, fontWeight: 600, fontFamily: FF,
          cursor: "pointer", outline: "none", transition: "border-color 0.12s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
      >
        Clear search
      </button>
    )}
  </div>
)

// ── Error state ───────────────────────────────────────────────────────────────
const ErrorState = ({ error }) => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "64px 24px",
    border: "0.5px solid #fecaca", borderRadius: T.radiusCard,
    background: "#fef2f2", textAlign: "center",
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: T.radiusCard,
      border: "0.5px solid #fecaca", background: "#fff1f2",
      display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16,
    }}>
      <X size={18} color="#fca5a5" />
    </div>
    <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6, fontFamily: FF }}>Something went wrong</p>
    <p style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>{error}</p>
  </div>
)

// ── Confirm remove modal ──────────────────────────────────────────────────────
const ConfirmRemoveDialog = ({ memberEmail, onConfirm, onCancel }) => (
  <div style={{
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.32)", display: "flex",
    alignItems: "center", justifyContent: "center", zIndex: 50,
    backdropFilter: "blur(1px)",
  }}>
    <div style={{
      background: T.bg, border: T.border,
      borderRadius: T.radiusCard, boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      width: "100%", maxWidth: 400, margin: "0 16px", fontFamily: FF,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: T.border }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FF }}>Remove member</span>
        <button onClick={onCancel} style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", color: T.textTertiary, outline: "none" }}>
          <X size={14} />
        </button>
      </div>
      <div style={{ padding: "16px 20px" }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.textMuted, lineHeight: 1.6, margin: 0 }}>
          Remove <span style={{ fontWeight: 700, color: T.text }}>{memberEmail}</span> from your organization? This action cannot be undone.
        </p>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "12px 20px", borderTop: T.border }}>
        <button
          onClick={onCancel}
          style={{
            height: 34, padding: "0 14px", borderRadius: T.radius,
            border: T.border, background: T.bg, color: T.textMuted,
            fontSize: 12, fontWeight: 600, fontFamily: FF,
            cursor: "pointer", outline: "none", transition: "border-color 0.12s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{
            height: 34, padding: "0 14px", borderRadius: T.radius,
            border: "none", background: "#dc2626", color: "#fff",
            fontSize: 12, fontWeight: 600, fontFamily: FF,
            cursor: "pointer", outline: "none", transition: "background 0.12s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#b91c1c" }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#dc2626" }}
        >
          Remove
        </button>
      </div>
    </div>
  </div>
)

// ── Edit role modal ───────────────────────────────────────────────────────────
const EditMemberDialog = ({
  memberEmail, editMemberRole, setEditMemberRole,
  editMemberStatus, isUpdatingMember,
  onUpdate, onCancel, isRoleUnchanged,
}) => (
  <div style={{
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.32)", display: "flex",
    alignItems: "center", justifyContent: "center", zIndex: 50,
    backdropFilter: "blur(1px)",
  }}>
    <div style={{
      background: T.bg, border: T.border,
      borderRadius: T.radiusCard, boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      width: "100%", maxWidth: 400, margin: "0 16px", fontFamily: FF,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: T.border }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Edit role</span>
        <button onClick={onCancel} style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", color: T.textTertiary, outline: "none" }}>
          <X size={14} />
        </button>
      </div>

      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, margin: 0, lineHeight: 1.6 }}>
          Updating role for <span style={{ fontWeight: 700, color: T.text }}>{memberEmail}</span>
        </p>

        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: T.textTertiary, fontFamily: MONO, marginBottom: 6 }}>
            Role
          </label>
          <div style={{ position: "relative" }}>
            <select
              value={editMemberRole}
              onChange={(e) => setEditMemberRole(e.target.value)}
              disabled={isUpdatingMember}
              style={{
                width: "100%", height: 36, padding: "0 32px 0 12px",
                appearance: "none", border: T.border,
                borderRadius: T.radius, background: T.bg,
                fontSize: 13, fontWeight: 500, color: T.text,
                fontFamily: FF, outline: "none", cursor: "pointer",
                transition: "border-color 0.12s",
                opacity: isUpdatingMember ? 0.5 : 1,
              }}
              onFocus={(e) => { e.target.style.borderColor = "#9ca3af" }}
              onBlur={(e) => { e.target.style.borderColor = "#e5e7eb" }}
            >
              <option value="interviewer">Interviewer</option>
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="recruiter">Recruiter</option>
            </select>
            <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.textTertiary }}>
              <ChevronDown size={13} />
            </div>
          </div>
        </div>

        {editMemberStatus && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 12px", borderRadius: T.radius,
            fontSize: 13, fontWeight: 500, fontFamily: FF,
            background:
              editMemberStatus.type === "error"   ? "#fef2f2" :
              editMemberStatus.type === "success" ? "#ecfdf5" : "#eef2ff",
            color:
              editMemberStatus.type === "error"   ? "#b91c1c" :
              editMemberStatus.type === "success" ? "#065f46" : "#4338ca",
            border: `0.5px solid ${
              editMemberStatus.type === "error"   ? "#fecaca" :
              editMemberStatus.type === "success" ? "#a7f3d0" : "#c7d2fe"
            }`,
          }}>
            {editMemberStatus.type === "error"   && <AlertCircle size={13} style={{ flexShrink: 0 }} />}
            {editMemberStatus.type === "success" && <Check size={13} style={{ flexShrink: 0 }} />}
            {editMemberStatus.type === "loading" && <Loader2 size={13} style={{ flexShrink: 0, animation: "org-spin 0.8s linear infinite" }} />}
            {editMemberStatus.message}
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "12px 20px", borderTop: T.border }}>
        <button
          onClick={onCancel}
          disabled={isUpdatingMember}
          style={{
            height: 34, padding: "0 14px", borderRadius: T.radius,
            border: T.border, background: T.bg, color: T.textMuted,
            fontSize: 12, fontWeight: 600, fontFamily: FF,
            cursor: isUpdatingMember ? "not-allowed" : "pointer",
            outline: "none", opacity: isUpdatingMember ? 0.5 : 1, transition: "border-color 0.12s",
          }}
          onMouseEnter={(e) => { if (!isUpdatingMember) e.currentTarget.style.borderColor = "#9ca3af" }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
        >
          Cancel
        </button>
        <button
          onClick={onUpdate}
          disabled={isUpdatingMember || isRoleUnchanged}
          style={{
            height: 34, padding: "0 14px", borderRadius: T.radius,
            border: "none",
            background: isUpdatingMember || isRoleUnchanged ? "#9ca3af" : "#111827",
            color: "#fff",
            fontSize: 12, fontWeight: 600, fontFamily: FF,
            cursor: isUpdatingMember || isRoleUnchanged ? "not-allowed" : "pointer",
            outline: "none", transition: "background 0.12s",
            display: "flex", alignItems: "center", gap: 6,
          }}
          onMouseEnter={(e) => { if (!isUpdatingMember && !isRoleUnchanged) e.currentTarget.style.background = "#27272a" }}
          onMouseLeave={(e) => { if (!isUpdatingMember && !isRoleUnchanged) e.currentTarget.style.background = "#111827" }}
        >
          {isUpdatingMember && <Loader2 size={12} style={{ animation: "org-spin 0.8s linear infinite" }} />}
          {isUpdatingMember ? "Saving…" : "Update role"}
        </button>
      </div>
    </div>
  </div>
)

// ── Tab bar ───────────────────────────────────────────────────────────────────
const TABS = (members) => [
  { id: "all",         label: "All",         count: members.length },
  { id: "active",      label: "Active",       count: members.filter((m) => m.status === "active").length },
  { id: "pending",     label: "Pending",      count: members.filter((m) => m.status === "pending").length },
  { id: "admin",       label: "Admins",       count: members.filter((m) => m.role === "admin").length },
  { id: "interviewer", label: "Interviewers", count: members.filter((m) => m.role === "interviewer").length },
]

// ── Main component ────────────────────────────────────────────────────────────
const OrganizationTab = () => {
  const { user } = useAuth()

  const [organization, setOrganization]     = useState(null)
  const [members, setMembers]               = useState([])
  const [isLoading, setIsLoading]           = useState(true)
  const [loadError, setLoadError]           = useState(null)
  const [activeTab, setActiveTab]           = useState("all")
  const [searchQuery, setSearchQuery]       = useState("")

  const [showAddUserModal, setShowAddUserModal]   = useState(false)
  const [addMemberStatus, setAddMemberStatus]     = useState(null)

  const [memberToRemove, setMemberToRemove]       = useState(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const [memberToEdit, setMemberToEdit]           = useState(null)
  const [showEditDialog, setShowEditDialog]       = useState(false)
  const [editMemberRole, setEditMemberRole]       = useState("")
  const [isUpdatingMember, setIsUpdatingMember]   = useState(false)
  const [editMemberStatus, setEditMemberStatus]   = useState(null)

  const [openDropdownId, setOpenDropdownId] = useState(null)
  const [dropdownPos, setDropdownPos]       = useState(null)

  // ── Load org data ──
  useEffect(() => {
    const fetchOrgData = async () => {
      if (!user?.uid) return
      setIsLoading(true)
      setLoadError(null)
      try {
        const orgData = await OrganizationService.getUserOrganization(user.uid)
        if (orgData) {
          setOrganization(orgData)
          const membersData = await OrganizationService.getOrganizationMembers(orgData.id)
          const withStatus = membersData.map((m) => ({
            ...m,
            status: m.uid === user.uid ? "active" : Math.random() > 0.3 ? "active" : "pending",
            name: m.name || m.email.split("@")[0].replace(/[^a-zA-Z]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
          }))
          setMembers(withStatus)
        } else {
          const isAdmin = await OrganizationService.isUserOrgAdmin(user.uid)
          if (isAdmin) {
            const adminOrg = await OrganizationService.getOrganizationByAdmin(user.uid)
            if (adminOrg) {
              setOrganization(adminOrg)
              const membersData = await OrganizationService.getOrganizationMembers(adminOrg.id)
              setMembers(membersData)
            }
          }
        }
      } catch {
        setLoadError("Failed to load organization data. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }
    fetchOrgData()
  }, [user])

  // ── Close dropdown on outside click / scroll ──
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!e.target.closest(".org-menu")) { setOpenDropdownId(null); setDropdownPos(null) }
    }
    const onScroll = () => { setOpenDropdownId(null); setDropdownPos(null) }
    document.addEventListener("mousedown", onMouseDown)
    window.addEventListener("scroll", onScroll, true)
    return () => {
      document.removeEventListener("mousedown", onMouseDown)
      window.removeEventListener("scroll", onScroll, true)
    }
  }, [])

  const isUserAdmin = organization && user && organization.adminUid === user.uid

  // ── Filtered members ──
  const tabs = TABS(members)
  const filteredMembers = members.filter((m) => {
    const matchTab =
      activeTab === "all"         ? true :
      activeTab === "active"      ? m.status === "active" :
      activeTab === "pending"     ? m.status === "pending" :
      activeTab === "admin"       ? m.role === "admin" :
      activeTab === "interviewer" ? m.role === "interviewer" : true
    const q = searchQuery.toLowerCase()
    const matchSearch = !q || m.email.toLowerCase().includes(q) || (m.name || "").toLowerCase().includes(q)
    return matchTab && matchSearch
  })

  // ── Dropdown open handler ──
  const openMenu = (uid, e) => {
    e.stopPropagation()
    if (openDropdownId === uid) { setOpenDropdownId(null); setDropdownPos(null); return }
    const r = e.currentTarget.getBoundingClientRect()
    const dropdownHeight = 100
    const spaceBelow = window.innerHeight - r.bottom
    const rightOffset = window.innerWidth - r.right
    if (spaceBelow < dropdownHeight && r.top > dropdownHeight) {
      setDropdownPos({ bottom: window.innerHeight - r.top + 4, top: undefined, right: rightOffset })
    } else {
      setDropdownPos({ top: r.bottom + 4, bottom: undefined, right: rightOffset })
    }
    setOpenDropdownId(uid)
  }

  // ── Handlers ──
  const handleAddUser = async (userData) => {
    setAddMemberStatus({ type: "loading", message: "Sending invitation…" })
    try {
      await InvitationService.createInvitation(
        organization.id, organization.name,
        userData.email, userData.firstName + " " + userData.lastName,
        userData.phoneNumber, userData.role, user?.uid,
      )
      setAddMemberStatus({ type: "success", message: `Invitation sent to ${userData.email}` })
      setTimeout(() => { setShowAddUserModal(false); setAddMemberStatus(null) }, 2000)
    } catch {
      setAddMemberStatus({ type: "error", message: "Failed to send invitation. Please try again." })
    }
  }

  const promptRemoveMember = (uid, email) => {
    setMemberToRemove({ uid, email })
    setShowConfirmDialog(true)
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove) return
    try {
      await OrganizationService.removeMember(organization.id, memberToRemove.uid)
      const data = await OrganizationService.getOrganizationMembers(organization.id)
      setMembers(data)
      setShowConfirmDialog(false)
      setMemberToRemove(null)
    } catch (err) {
      alert("Failed to remove member. " + err.message)
    }
  }

  const promptEditMember = (member) => {
    setMemberToEdit({ uid: member.uid, email: member.email, currentRole: member.role })
    setEditMemberRole(member.role)
    setEditMemberStatus(null)
    setShowEditDialog(true)
  }

  const handleUpdateMember = async () => {
    if (!memberToEdit || editMemberRole === memberToEdit.currentRole) { setShowEditDialog(false); return }
    setIsUpdatingMember(true)
    setEditMemberStatus({ type: "loading", message: "Updating role…" })
    try {
      await OrganizationService.updateMemberRole(organization.id, memberToEdit.uid, editMemberRole)
      const data = await OrganizationService.getOrganizationMembers(organization.id)
      setMembers(data)
      setEditMemberStatus({ type: "success", message: "Role updated successfully" })
      setTimeout(() => { setShowEditDialog(false); setMemberToEdit(null); setEditMemberStatus(null) }, 1500)
    } catch {
      setEditMemberStatus({ type: "error", message: "Failed to update role. Please try again." })
    } finally {
      setIsUpdatingMember(false)
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bgPage, fontFamily: FF }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes org-spin { to { transform: rotate(360deg); } }
        .org-scroll::-webkit-scrollbar { display: none; }
        .org-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div style={{ display: "flex" }}>
        {/* Sidebar */}
        <div style={{ borderRight: T.border, background: T.bg, minHeight: "100vh", overflowY: "auto" }}>
          <ProfileSidebar activeTab="organization" setActiveTab={() => {}} />
        </div>

        {/* Main */}
        <div
          className="org-scroll"
          style={{ flex: 1, marginLeft: DESKTOP_SIDEBAR_WIDTH, overflowY: "auto", width: "100%" }}
        >
          <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>

            {/* ── Top bar ── */}
            <div style={{
              background: T.bg, borderBottom: T.border,
              padding: "13px 20px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexShrink: 0, position: "sticky", top: 0, zIndex: 20,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#111827", flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textTertiary, fontFamily: MONO }}>
                  Members
                </span>
                <span style={{
                  fontSize: 12, fontWeight: 600, color: T.textTertiary,
                  background: T.bgMuted, border: T.border,
                  borderRadius: 5, padding: "1px 7px", fontFamily: MONO,
                }}>
                  {filteredMembers.length}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Search */}
                <div style={{ position: "relative", height: 34 }}>
                  <div style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex", alignItems: "center" }}>
                    <Search size={13} color={T.textTertiary} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search members…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      height: "100%", paddingLeft: 30, paddingRight: searchQuery ? 28 : 12,
                      width: 200, border: T.border, borderRadius: T.radius,
                      fontSize: 13, fontWeight: 500, color: T.text,
                      fontFamily: FF, background: T.bg, outline: "none", transition: "border-color 0.12s",
                    }}
                    onFocus={(e) => { e.target.style.borderColor = "#9ca3af" }}
                    onBlur={(e)  => { e.target.style.borderColor = "#e5e7eb" }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.textTertiary, display: "flex", alignItems: "center", padding: 0, outline: "none" }}
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>

                {isUserAdmin && (
                  <button
                    onClick={() => setShowAddUserModal(true)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      height: 34, padding: "0 14px", borderRadius: T.radius,
                      border: "none", background: "#111827", color: "#fff",
                      fontSize: 12, fontWeight: 600, fontFamily: FF,
                      cursor: "pointer", transition: "background 0.12s", outline: "none",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#27272a" }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#111827" }}
                  >
                    <UserPlus size={12} strokeWidth={2.5} />
                    Add member
                  </button>
                )}
              </div>
            </div>

            {/* ── Tab bar ── */}
            <div style={{ background: T.bg, borderBottom: T.border, padding: "0 20px", display: "flex", alignItems: "center", gap: 0 }}>
              {tabs.map((tab) => {
                const active = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 7,
                      padding: "0 14px", height: 44, border: "none",
                      background: "transparent", cursor: "pointer", outline: "none",
                      position: "relative", transition: "color 0.12s",
                      fontSize: 13, fontWeight: 600, fontFamily: FF,
                      color: active ? T.text : T.textTertiary,
                    }}
                  >
                    {tab.label}
                    <span style={{
                      fontSize: 12, fontWeight: 600,
                      background: active ? T.bgMuted : "transparent",
                      border: active ? T.border : "0.5px solid transparent",
                      borderRadius: 5, padding: "1px 7px", fontFamily: MONO,
                      color: active ? T.textMuted : T.textTertiary,
                      transition: "all 0.12s",
                    }}>
                      {tab.count}
                    </span>
                    {active && (
                      <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: "#111827", borderRadius: "2px 2px 0 0" }} />
                    )}
                  </button>
                )
              })}
            </div>

            {/* ── Content ── */}
            <div className="org-scroll" style={{ flex: 1, overflowY: "auto" }}>
              <div style={{ padding: "20px 20px 48px" }}>
                {isLoading ? (
                  <div style={{ background: T.bg, borderRadius: T.radiusCard, border: T.border, boxShadow: T.shadow, overflowX: "auto" }}>
                    <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                      <TableHeader />
                      <TableShimmer rows={6} />
                    </div>
                  </div>
                ) : loadError ? (
                  <ErrorState error={loadError} />
                ) : filteredMembers.length === 0 ? (
                  <EmptyState searchQuery={searchQuery} onClear={() => setSearchQuery("")} />
                ) : (
                  <div style={{ background: T.bg, borderRadius: T.radiusCard, border: T.border, boxShadow: T.shadow, overflow: "visible" }}>
                    <div style={{ overflowX: "auto", overflowY: "visible", borderRadius: T.radiusCard }}>
                      <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                        <TableHeader />
                        {filteredMembers.map((member, idx) => (
                          <MemberRow
                            key={member.uid}
                            member={member}
                            isCurrentUser={member.uid === user?.uid}
                            isAdmin={isUserAdmin}
                            isLast={idx === filteredMembers.length - 1}
                            openDropdownId={openDropdownId}
                            dropdownPos={dropdownPos}
                            onOpenMenu={openMenu}
                            onCloseMenu={() => { setOpenDropdownId(null); setDropdownPos(null) }}
                            onEdit={promptEditMember}
                            onRemove={promptRemoveMember}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {!isLoading && !loadError && filteredMembers.length > 0 && (
                  <div style={{ marginTop: 12, paddingLeft: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>
                      {filteredMembers.length} of {members.length} {members.length === 1 ? "member" : "members"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => { setShowAddUserModal(false); setAddMemberStatus(null) }}
        onAddUser={handleAddUser}
        addMemberStatus={addMemberStatus}
        isLoading={addMemberStatus?.type === "loading"}
      />

      {showConfirmDialog && (
        <ConfirmRemoveDialog
          memberEmail={memberToRemove?.email}
          onConfirm={handleRemoveMember}
          onCancel={() => { setShowConfirmDialog(false); setMemberToRemove(null) }}
        />
      )}

      {showEditDialog && (
        <EditMemberDialog
          memberEmail={memberToEdit?.email}
          editMemberRole={editMemberRole}
          setEditMemberRole={setEditMemberRole}
          editMemberStatus={editMemberStatus}
          isUpdatingMember={isUpdatingMember}
          onUpdate={handleUpdateMember}
          onCancel={() => { setShowEditDialog(false); setMemberToEdit(null); setEditMemberRole(""); setEditMemberStatus(null) }}
          isRoleUnchanged={editMemberRole === memberToEdit?.currentRole}
        />
      )}
    </div>
  )
}

export default OrganizationTab